/**
 * Merge-token metadata for the staff rich-text editor's "Insert field"
 * dropdown (see apps/web/src/components/RichTextEditor.tsx). Every token
 * listed here must be a real key produced by `resolveNoticeMergeFields`/
 * `resolveLeaseMergeFields` in apps/api/src/lib/document-merge-fields.ts —
 * a drift-guard test in apps/api/test/document-merge-fields.test.ts asserts
 * exactly that, so a typo here fails a test instead of silently rendering
 * `[[unknown token: x]]` inside a real generated document.
 */
export const COMPANY_MERGE_TOKENS = [
    { token: 'company.legalName', label: 'Company legal name' },
    { token: 'company.mailingLine1', label: 'Company mailing address, line 1' },
    { token: 'company.mailingLine2', label: 'Company mailing address, line 2' },
    { token: 'company.mailingCity', label: 'Company mailing city' },
    { token: 'company.mailingState', label: 'Company mailing state' },
    { token: 'company.mailingPostalCode', label: 'Company mailing ZIP' },
    { token: 'company.mailingAddress', label: 'Company mailing address (single line)' },
    { token: 'company.phone', label: 'Company phone' },
    { token: 'company.licenseNumber', label: 'Company license / registration number' },
    { token: 'company.signatoryName', label: 'Signatory name' },
    { token: 'company.signatoryTitle', label: 'Signatory title' },
    { token: 'company.contactEmail', label: 'Company contact email' },
    { token: 'company.lateFee5DayAmount', label: 'Late fee — 5 days late' },
    { token: 'company.lateFee10DayAmount', label: 'Late fee — 10 days late (additional)' },
    {
        token: 'company.habitualLateFeeAmount',
        label: 'Habitual late fee (6th consecutive late month)',
    },
    { token: 'company.nsfFeeAmount', label: 'NSF / returned payment fee' },
];
const OTHER_MERGE_TOKENS = [{ token: 'today', label: "Today's date" }];
/**
 * Notice-context tokens — used for every DocumentTemplateKind except
 * LEASE_AGREEMENT, OWNER_STATEMENT, and DECLINE_NOTICE (which has its own
 * group below — it has no lease/tenant/notice-kind context of its own).
 */
export const NOTICE_MERGE_TOKEN_GROUPS = [
    {
        category: 'Property',
        tokens: [
            { token: 'property.name', label: 'Property name' },
            { token: 'unit.label', label: 'Unit label' },
        ],
    },
    {
        category: 'Tenant',
        tokens: [{ token: 'tenant.names', label: 'Tenant name(s)' }],
    },
    {
        category: 'Notice',
        tokens: [
            { token: 'notice.kind', label: 'Notice kind' },
            { token: 'notice.triggeredOn', label: 'Notice triggered date' },
            { token: 'notice.dueBy', label: 'Notice due-by date' },
            { token: 'lease.id', label: 'Lease ID' },
        ],
    },
    { category: 'Company', tokens: COMPANY_MERGE_TOKENS },
    { category: 'Other', tokens: OTHER_MERGE_TOKENS },
];
/**
 * DECLINE_NOTICE's own tokens — a rejected applicant, not a lease or tenant,
 * is the subject. `decision.criterion` is the firm's own published
 * `ScreeningCriterion` text the applicant failed (invariant 8: never free
 * text); it renders as `—` when the decision didn't cite one.
 */
export const DECLINE_NOTICE_MERGE_TOKEN_GROUPS = [
    {
        category: 'Applicant',
        tokens: [{ token: 'applicant.name', label: 'Applicant name' }],
    },
    {
        category: 'Property',
        tokens: [
            { token: 'property.name', label: 'Property name' },
            { token: 'unit.label', label: 'Unit label' },
        ],
    },
    {
        category: 'Decision',
        tokens: [
            { token: 'decision.criterion', label: 'Criterion that failed' },
            { token: 'decision.decidedOn', label: 'Decision date' },
        ],
    },
    { category: 'Company', tokens: COMPANY_MERGE_TOKENS },
    { category: 'Other', tokens: OTHER_MERGE_TOKENS },
];
/**
 * The two transactional-email kinds (PORTAL_INVITE_EMAIL/PASSWORD_RESET_EMAIL)
 * — deliberately narrow. There is no token for the CTA URL/button or the
 * expiry footer: those stay fixed, code-rendered, never part of the
 * template body (see DocumentTemplateKind's doc comment in schema.prisma).
 */
export const TRANSACTIONAL_EMAIL_TOKEN_GROUPS = [
    {
        category: 'Recipient',
        tokens: [{ token: 'recipient.name', label: 'Recipient name' }],
    },
    { category: 'Company', tokens: COMPANY_MERGE_TOKENS },
    { category: 'Other', tokens: OTHER_MERGE_TOKENS },
];
export const LEASE_MERGE_TOKEN_GROUPS = [
    {
        category: 'Property',
        tokens: [
            { token: 'property.name', label: 'Property name' },
            { token: 'unit.label', label: 'Unit label' },
        ],
    },
    {
        category: 'Tenant',
        tokens: [
            { token: 'tenant.names', label: 'Tenant name(s)' },
            { token: 'tenant.primaryName', label: 'Primary tenant name' },
        ],
    },
    {
        category: 'Lease terms',
        tokens: [
            { token: 'lease.status', label: 'Lease status' },
            { token: 'lease.startDate', label: 'Lease start date' },
            { token: 'lease.endDate', label: 'Lease end date' },
            { token: 'lease.monthlyRent', label: 'Monthly rent' },
            { token: 'lease.securityDeposit', label: 'Security deposit' },
            { token: 'lease.rentDueDay', label: 'Rent due day' },
        ],
    },
    {
        category: 'Owner',
        tokens: [
            { token: 'owner.names', label: 'Owner name(s)' },
            { token: 'owner.mailingAddress', label: 'Owner mailing address' },
        ],
    },
    {
        category: 'Household',
        tokens: [
            { token: 'household.dependents', label: 'Dependents (name and relationship)' },
            { token: 'household.pets', label: 'Pets (name, species, breed)' },
        ],
    },
    { category: 'Company', tokens: COMPANY_MERGE_TOKENS },
    { category: 'Other', tokens: OTHER_MERGE_TOKENS },
];
//# sourceMappingURL=document-merge-tokens.js.map